// TaskFilterStatusBar.tsx - Simple & Safe Filter Indicator
import React from 'react';
import { X, Calendar, Users, User, CheckSquare, Building, ArrowUpDown } from 'lucide-react';
import styled from 'styled-components';
import moment from 'moment';
import { hasFullAdminAccess } from '../../utils/roleUtils';

interface TaskFilterStatusBarProps {
  paramState: any; // Current filter parameters
  storeData: any; // Redux store data for lookups
  onRemoveFilter: (filterKey: string, filterValue?: string) => void;
  onClearAll: () => void;
  onEditFilter?: () => void; // Optional: opens filter drawer
}

const TaskFilterStatusBar: React.FC<TaskFilterStatusBarProps> = ({
  paramState,
  storeData,
  onRemoveFilter,
  onClearAll,
  onEditFilter
}) => {
  
  // ✅ SAFE: Check if we have any filters to show
  const hasActiveFilters = () => {
    if (!paramState) return false;

    // Ignore these keys (not filters) - but include orderBy if non-default
    const ignoredKeys = ['page', 'perPage', 'status'];

    // Check for sorting - only show if explicitly set to non-default
    const hasCustomSort = paramState.orderBy && paramState.orderBy !== 'toBePerformAt';
    const hasCustomDirection = paramState.isAscending !== undefined && paramState.isAscending !== true;

    if (hasCustomSort || hasCustomDirection) return true;

    return Object.keys(paramState).some(key => {
      if (ignoredKeys.includes(key)) return false;
      if (key === 'orderBy' || key === 'isAscending') return false; // handled above

      const value = paramState[key];

      // Check for meaningful values
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return value === true;
      if (typeof value === 'string') return value.length > 0;
      if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;

      return value !== undefined && value !== null;
    });
  };

  // ✅ Get sort field display name
  const getSortFieldName = (orderBy: string): string => {
    const sortFields: Record<string, string> = {
      'type': 'Type',
      'toBePerformAt': 'Due Date',
      'createdBy': 'Created By',
      '_id': 'Default'
    };
    return sortFields[orderBy] || orderBy;
  };

  // ✅ Get sort direction display name
  const getSortDirectionName = (isAscending: boolean | undefined): string => {
    if (isAscending === true) return 'Ascending';
    if (isAscending === false) return 'Descending';
    return '';
  };

  // ✅ SAFE: Get team name with fallback
  const getTeamName = (teamId: string): string => {
    try {
      const teams = storeData?.userData?.userDetails?.organizationTeams || [];
      const team = teams.find((t: any) => t._id === teamId);
      return team?.name || `Team ${teamId.slice(-4)}`;
    } catch (e) {
      return `Team ${teamId.slice(-4)}`;
    }
  };

  // ✅ FIX: Check if team ID is actually the organization ID
  const isOrganizationId = (teamId: string): boolean => {
    try {
      const orgId = storeData?.userData?.userDetails?.organization?._id;
      return teamId === orgId;
    } catch (e) {
      return false;
    }
  };

  // ✅ SAFE: Get user name with fallback - uses subordinate data for security
  const getUserName = (userId: string): string => {
    try {
      // 🔒 SECURITY: Get filtered employee list based on role
      const userRole = storeData?.userData?.userDetails?.role?.name;
      const subordinateData = storeData?.userData?.userDetails?.subordinateEmployees;

      let employees: any[] = [];

      if (hasFullAdminAccess(storeData?.userData?.userDetails)) {
        employees = storeData?.userData?.userDetails?.organizationEmployee || [];
      } else {
        // Combine subordinate arrays
        const reportTo = subordinateData?.reportTo || [];
        const teamMembers = subordinateData?.teamMembers || [];
        const orgMembers = subordinateData?.organizationMember || [];
        employees = [...reportTo, ...teamMembers, ...orgMembers];

        // Add current user if not in list
        const currentUserId = storeData?.userData?.userDetails?._id;
        if (currentUserId && !employees.some((emp: any) => emp._id === currentUserId)) {
          employees.push({
            _id: currentUserId,
            firstName: storeData?.userData?.userDetails?.firstName || '',
            lastName: storeData?.userData?.userDetails?.lastName || ''
          });
        }
      }

      const user = employees.find((emp: any) => emp._id === userId);
      if (user) {
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || userId.slice(-4);
      }
      return `User ${userId.slice(-4)}`;
    } catch (e) {
      return `User ${userId.slice(-4)}`;
    }
  };

  // ✅ SAFE: Get task type name with fallback
  const getTaskTypeName = (taskType: string): string => {
    try {
      const taskTypes = storeData?.user?.userPreferences?.taskType || [];
      const type = taskTypes.find((t: any) => t.value === taskType);
      return type?.name || taskType;
    } catch (e) {
      return taskType;
    }
  };

  // ✅ SAFE: Format date range with fallback
  const formatDateRange = (startDate: string, endDate: string): string => {
    try {
      const start = moment(startDate);
      const end = moment(endDate);
      
      if (!start.isValid() || !end.isValid()) {
        return 'Custom Date Range';
      }
      
      // Same day
      if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
        return start.format('MMM DD, YYYY');
      }
      
      // Different days
      return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
    } catch (e) {
      return 'Custom Date Range';
    }
  };

  // ✅ SAFE: Don't render if no filters
  if (!hasActiveFilters()) {
    return null;
  }

  return (
    <FilterBarContainer>
      <FilterContent>
        <FilterLabel>Active Filters:</FilterLabel>
        
        {/* Team Filter - Only show if it's a real team (not organization) */}
        {paramState.team && !isOrganizationId(paramState.team) && !paramState.byOrganization && (
          <FilterChip color="blue" onClick={onEditFilter}>
            <Users size={14} />
            <span>Team: {getTeamName(paramState.team)}</span>
            <RemoveButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onRemoveFilter('team');
              }}
            >
              <X size={14} />
            </RemoveButton>
          </FilterChip>
        )}

        {/* Organization Filter */}
        {paramState.byOrganization && (
          <FilterChip color="indigo" onClick={onEditFilter}>
            <Building size={14} />
            <span>All {storeData?.userData?.userDetails?.organization?.name || 'Organization'}</span>
            <RemoveButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onRemoveFilter('byOrganization');
              }}
            >
              <X size={14} />
            </RemoveButton>
          </FilterChip>
        )}

        {/* Team Member Filter */}
        {paramState.userId && (
          <FilterChip color="purple" onClick={onEditFilter}>
            <User size={14} />
            <span>Member: {getUserName(paramState.userId)}</span>
            <RemoveButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onRemoveFilter('userId');
              }}
            >
              <X size={14} />
            </RemoveButton>
          </FilterChip>
        )}

        {/* Task Assignment Filter */}
        {paramState.taskAssign && (
          <FilterChip color="orange" onClick={onEditFilter}>
            <User size={14} />
            <span>Assigned: {getUserName(paramState.taskAssign)}</span>
            <RemoveButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onRemoveFilter('taskAssign');
              }}
            >
              <X size={14} />
            </RemoveButton>
          </FilterChip>
        )}

        {/* Task Type Filter */}
        {paramState.taskType && (
          <FilterChip color="green" onClick={onEditFilter}>
            <CheckSquare size={14} />
            <span>Type: {getTaskTypeName(paramState.taskType)}</span>
            <RemoveButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onRemoveFilter('taskType');
              }}
            >
              <X size={14} />
            </RemoveButton>
          </FilterChip>
        )}

        {/* Date Range Filter */}
        {(paramState.startedAt && paramState.endedAt) && (
          <FilterChip color="cyan" onClick={onEditFilter}>
            <Calendar size={14} />
            <span>Due: {formatDateRange(paramState.startedAt, paramState.endedAt)}</span>
            <RemoveButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onRemoveFilter('startedAt'); // This should remove both startedAt and endedAt
              }}
            >
              <X size={14} />
            </RemoveButton>
          </FilterChip>
        )}

        {/* Sort Settings Filter - Show if non-default sorting is applied */}
        {(paramState.orderBy || paramState.isAscending !== undefined) && (
          <FilterChip color="gray" onClick={onEditFilter}>
            <ArrowUpDown size={14} />
            <span>
              Sort: {getSortFieldName(paramState.orderBy || 'toBePerformAt')}
              {paramState.isAscending !== undefined && ` (${getSortDirectionName(paramState.isAscending)})`}
            </span>
            <RemoveButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onRemoveFilter('orderBy');
                onRemoveFilter('isAscending');
              }}
            >
              <X size={14} />
            </RemoveButton>
          </FilterChip>
        )}

        {/* Clear All Button */}
        <ClearButton onClick={onClearAll}>
          <X size={14} />
          Clear all
        </ClearButton>
      </FilterContent>
    </FilterBarContainer>
  );
};

// ✅ STYLED COMPONENTS (Reused from Lead Filter)
const FilterBarContainer = styled.div`
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 12px;
  margin-bottom: 8px;
`;

const FilterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const FilterLabel = styled.span`
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  margin-right: 8px;
`;

const FilterChip = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  gap: 6px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  max-width: 300px;
  
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  ${({ color }) => {
    switch (color) {
      case 'blue':
        return `
          border: 1px solid #bfdbfe;
          color: #1e40af;
          &:hover { background-color: #eff6ff; }
          svg { color: #3b82f6; }
        `;
      case 'green':
        return `
          border: 1px solid #bbf7d0;
          color: #166534;
          &:hover { background-color: #f0fdf4; }
          svg { color: #22c55e; }
        `;
      case 'purple':
        return `
          border: 1px solid #ddd6fe;
          color: #5b21b6;
          &:hover { background-color: #f5f3ff; }
          svg { color: #8b5cf6; }
        `;
      case 'orange':
        return `
          border: 1px solid #fed7aa;
          color: #9a3412;
          &:hover { background-color: #fff7ed; }
          svg { color: #f97316; }
        `;
      case 'indigo':
        return `
          border: 1px solid #c7d2fe;
          color: #3730a3;
          &:hover { background-color: #eef2ff; }
          svg { color: #6366f1; }
        `;
      case 'cyan':
        return `
          border: 1px solid #a5f3fc;
          color: #155e75;
          &:hover { background-color: #ecfeff; }
          svg { color: #06b6d4; }
        `;
      case 'gray':
        return `
          border: 1px solid #d1d5db;
          color: #374151;
          &:hover { background-color: #f9fafb; }
          svg { color: #6b7280; }
        `;
      default:
        return `
          border: 1px solid #e2e8f0;
          color: #475569;
          &:hover { background-color: #f8fafc; }
          svg { color: #64748b; }
        `;
    }
  }}
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  min-width: 16px;
  transition: color 0.2s;
  
  &:hover {
    color: #ef4444;
  }
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  background: none;
  border: none;
  color: #ef4444;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  
  &:hover {
    background-color: #fef2f2;
  }
`;

export default TaskFilterStatusBar;